'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface DeviceContextType {
  isMobile: boolean
  isDesktop: boolean
  isHydrated: boolean
  viewportWidth: number
}

const DeviceContext = createContext<DeviceContextType>({
  isMobile: false,
  isDesktop: true,
  isHydrated: false,
  viewportWidth: 0
})

export const MOBILE_BREAKPOINT_PX = 1040

// Routes that should fold into the mobile shell when the desktop viewport is
// narrower than MOBILE_BREAKPOINT_PX. Other routes keep the UA-only behavior.
//
// '/create' is included so the v2 Team Creation Wizard renders its dedicated
// mobile shells (TeamNameMobile, GameModesMobile, LeaguesMobile, ShopMobile)
// when a desktop browser is resized below MOBILE_BREAKPOINT_PX — preserving the
// wizard's responsive mobile layout on narrow desktop widths, without forcing
// the rest of the app off Mizi's device-signal model.
export const NARROW_AS_MOBILE_ROUTES = ['/transfers', '/dashboard', '/create'] as const

export function shouldRouteUseNarrowAsMobile(
  pathname: string | null | undefined,
  viewportWidth: number
): boolean {
  if (!pathname) return false
  if (viewportWidth <= 0 || viewportWidth >= MOBILE_BREAKPOINT_PX) return false
  return NARROW_AS_MOBILE_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  )
}

interface DeviceDetectionInput {
  innerWidth: number
  maxTouchPoints?: number
  platform?: string
  userAgent?: string
  userAgentDataMobile?: boolean
}

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    mobile?: boolean
  }
}

// Use the mobile/tablet layouts for real mobile device signals. A desktop
// browser resized below the lg breakpoint should keep the desktop app shell.
const MOBILE_UA_REGEX = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i

export function shouldUseMobileLayoutForDevice({
  maxTouchPoints = 0,
  platform = '',
  userAgent = '',
  userAgentDataMobile = false
}: DeviceDetectionInput): boolean {
  if (userAgentDataMobile) return true
  if (MOBILE_UA_REGEX.test(userAgent)) return true
  // iPadOS 13+ reports as desktop Safari but exposes touch; treat as mobile.
  const isIPadOS = platform === 'MacIntel' && maxTouchPoints > 1
  return isIPadOS
}

function shouldUseMobileLayout(): boolean {
  if (typeof window === 'undefined') return false
  const userAgentData = (navigator as NavigatorWithUserAgentData).userAgentData
  return shouldUseMobileLayoutForDevice({
    innerWidth: window.innerWidth,
    maxTouchPoints: navigator.maxTouchPoints,
    platform: navigator.platform,
    userAgent: navigator.userAgent,
    userAgentDataMobile: userAgentData?.mobile
  })
}

export function DeviceProvider({ children }: { children: ReactNode }) {
  const [isHydrated, setIsHydrated] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [viewportWidth, setViewportWidth] = useState(0)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const updateDeviceLayout = () => {
      setIsMobile(shouldUseMobileLayout())
      setViewportWidth(window.innerWidth)
    }

    updateDeviceLayout()
    setIsHydrated(true)
    window.addEventListener('resize', updateDeviceLayout)

    return () => window.removeEventListener('resize', updateDeviceLayout)
  }, [])

  return (
    <DeviceContext.Provider
      value={{
        isMobile,
        isDesktop: !isMobile,
        isHydrated,
        viewportWidth
      }}
    >
      {children}
    </DeviceContext.Provider>
  )
}

export function useDevice() {
  const context = useContext(DeviceContext)
  if (context === undefined) {
    throw new Error('useDevice must be used within a DeviceProvider')
  }
  return context
}

export default DeviceProvider
